import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from "react"
import { useAuth } from "@/lib/auth-context"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Plus, PackageOpen } from "lucide-react"
import { API_BASE } from '@/lib/api-base'

interface ItemEstoque {
  id: string
  codigo: string | null
  nome: string
  categoria: string
  unidade: string
}

interface Galpao {
  id: string
  nome: string
  status: string
}

interface Lote {
  id: string
  codigo: string
  galpao_id: string
  status: string
}

interface ConsumoHistorico {
  id: string
  item_nome: string
  item_codigo: string | null
  quantidade: number
  estoque_nome: string
  galpao_nome: string
  lote_codigo: string | null
  saldo_anterior: number
  saldo_posterior: number
  data: string
  hora: string
  criado_em: string | null
}

function ConsumoPage() {
  const apiBase = API_BASE
  const { user, apiFetch, isSuperAdmin, selectedTenantId } = useAuth()

  const [itens, setItens] = useState<ItemEstoque[]>([])
  const [galpoes, setGalpoes] = useState<Galpao[]>([])
  const [lotes, setLotes] = useState<Lote[]>([])
  const [historico, setHistorico] = useState<ConsumoHistorico[]>([])
  const [showForm, setShowForm] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [lastConsumo, setLastConsumo] = useState<ConsumoHistorico | null>(null)

  const [form, setForm] = useState({
    item_id: "",
    quantidade: 0,
    galpao_id: "",
    lote_id: "",
    observacao: ""
  })

  const fetchItens = useCallback(async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/estoque/itens`)
      if (res.ok) {
        const data = await res.json() as ItemEstoque[]
        setItens(data.filter(i => ["Racao", "Medicamento", "Vacina"].includes(i.categoria)))
      }
    } catch (e) { console.error(e) }
  }, [apiFetch, apiBase])

  const fetchGalpoes = useCallback(async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/galpoes/`)
      if (res.ok) {
        setGalpoes((await res.json()).filter((g: Galpao) => g.status === "ativo"))
      }
    } catch (e) { console.error(e) }
  }, [apiFetch, apiBase])

  const fetchLotes = useCallback(async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/lotes/`)
      if (res.ok) {
        setLotes((await res.json()).filter((l: Lote) => l.status === "ativo"))
      }
    } catch (e) { console.error(e) }
  }, [apiFetch, apiBase])

  const fetchHistorico = useCallback(async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/consumo/historico`)
      if (res.ok) {
        setHistorico(await res.json())
      }
    } catch (e) { console.error(e) }
  }, [apiFetch, apiBase])

  useEffect(() => {
    if (!user) return
    if (isSuperAdmin && !selectedTenantId) return
    void fetchItens()
    void fetchGalpoes()
    void fetchLotes()
    void fetchHistorico()
  }, [user, isSuperAdmin, selectedTenantId, fetchItens, fetchGalpoes, fetchLotes, fetchHistorico])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)

    if (!form.item_id) {
      setSubmitError("Selecione o item (racao/vacina/medicamento)")
      return
    }
    if (!form.galpao_id) {
      setSubmitError("Selecione o galpao")
      return
    }
    if (form.quantidade <= 0) {
      setSubmitError("Quantidade deve ser maior que zero")
      return
    }

    const payload = {
      item_id: form.item_id,
      quantidade: form.quantidade,
      galpao_id: form.galpao_id,
      lote_id: form.lote_id || null,
      observacao: form.observacao || null,
    }

    try {
      const res = await apiFetch(`${apiBase}/api/consumo/rapido`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (res.ok) {
        const created = await res.json() as ConsumoHistorico
        setLastConsumo(created)
        setForm({
          item_id: itens[0]?.id || "",
          quantidade: 0,
          galpao_id: galpoes[0]?.id || "",
          lote_id: "",
          observacao: ""
        })
        setShowForm(false)
        fetchHistorico()
      } else {
        const errData = await res.json().catch(() => ({})) as { detail?: string }
        setSubmitError(errData.detail || "Erro ao registrar consumo")
      }
    } catch (e) {
      console.error(e)
      setSubmitError("Erro de conexao")
    }
  }

  const lotesDoGalpao = form.galpao_id
    ? lotes.filter(l => l.galpao_id === form.galpao_id)
    : lotes

  const itemSelecionado = itens.find(i => i.id === form.item_id)

  const formatNumber = (v: number, decimals = 3) => 
    new Intl.NumberFormat('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(v)

  const formatData = (data: string, hora?: string) => {
    const parts = data.split('-')
    if (parts.length === 3) {
      const formatted = `${parts[2]}/${parts[1]}/${parts[0]}`
      return hora ? `${formatted} ${hora}` : formatted
    }
    return data
  }

  if (isSuperAdmin && !selectedTenantId) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Selecione uma granja no menu lateral.</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h1 className="text-2xl font-bold">Consumo (Racao/Vacinas)</h1>
        <Button
          onClick={() => {
            setSubmitError(null)
            setShowForm(true)
            setForm({
              item_id: itens[0]?.id || "",
              quantidade: 0,
              galpao_id: galpoes[0]?.id || "",
              lote_id: lotes.find(l => l.galpao_id === galpoes[0]?.id)?.id || "",
              observacao: ""
            })
          }}
        >
          <Plus className="h-4 w-4 mr-2" /> Registrar Consumo
        </Button>
      </div>

      {lastConsumo && (
        <Card className="max-w-lg border-green-200 bg-green-50 dark:bg-green-950/20">
          <CardHeader>
            <CardTitle className="text-green-800 dark:text-green-200">Consumo registrado</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p><strong>Item:</strong> {lastConsumo.item_nome} ({lastConsumo.item_codigo || "-"})</p>
            <p><strong>Quantidade:</strong> {formatNumber(lastConsumo.quantidade)}</p>
            <p><strong>Galpao:</strong> {lastConsumo.galpao_nome}</p>
            {lastConsumo.lote_codigo && <p><strong>Plantel:</strong> {lastConsumo.lote_codigo}</p>}
            <p><strong>Saldo anterior:</strong> {formatNumber(lastConsumo.saldo_anterior)}</p>
            <p><strong>Saldo atual:</strong> {formatNumber(lastConsumo.saldo_posterior)}</p>
            <Button variant="ghost" size="sm" onClick={() => setLastConsumo(null)}>Fechar</Button>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={showForm}
        onOpenChange={(open) => {
          if (!open) setShowForm(false)
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto gap-0 p-0">
          <DialogTitle className="sr-only">Registrar Consumo</DialogTitle>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold pr-10">Registrar Consumo</h2>
            {submitError && (
              <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                {submitError}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Galpao *</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                  value={form.galpao_id}
                  onChange={(e) => setForm({ ...form, galpao_id: e.target.value, lote_id: lotes.find(l => l.galpao_id === e.target.value)?.id || "" })}
                  required
                >
                  {galpoes.map(g => (
                    <option key={g.id} value={g.id}>{g.nome}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label>Plantel (opcional)</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                  value={form.lote_id}
                  onChange={(e) => setForm({ ...form, lote_id: e.target.value })}
                >
                  <option value="">Selecione...</option>
                  {lotesDoGalpao.map(l => (
                    <option key={l.id} value={l.id}>{l.codigo}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label>Item (Racao/Vacina/Medicamento) *</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                  value={form.item_id}
                  onChange={(e) => setForm({ ...form, item_id: e.target.value })}
                  required
                >
                  {itens.map(i => (
                    <option key={i.id} value={i.id}>
                      {i.nome} ({i.categoria}) - {i.unidade}
                    </option>
                  ))}
                </select>
                {itens.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Nenhum item cadastrado. Va em Estoque &gt; Itens para cadastrar racao/vacina.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Quantidade {itemSelecionado ? `(${itemSelecionado.unidade})` : ""} *</Label>
                <Input
                  type="number"
                  step="0.001"
                  min="0.001"
                  value={form.quantidade || ""}
                  onChange={(e) => setForm({ ...form, quantidade: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Observacao</Label>
                <Input
                  value={form.observacao}
                  onChange={(e) => setForm({ ...form, observacao: e.target.value })}
                  placeholder="Opcional"
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit">Registrar</Button>
                <Button variant="outline" type="button" onClick={() => setShowForm(false)}>Cancelar</Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PackageOpen className="h-5 w-5" />
            Historico de Consumos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {historico.length === 0 ? (
            <p className="text-muted-foreground">Nenhum consumo registrado ainda.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Data</th>
                    <th className="text-left py-2">Item</th>
                    <th className="text-right py-2">Qtd</th>
                    <th className="text-left py-2">Galpao</th>
                    <th className="text-left py-2">Plantel</th>
                    <th className="text-right py-2">Saldo Ant.</th>
                    <th className="text-right py-2">Saldo Atual</th>
                  </tr>
                </thead>
                <tbody>
                  {historico.map((h) => (
                    <tr key={h.id} className="border-b">
                      <td className="py-2">{formatData(h.data, h.hora)}</td>
                      <td className="py-2">{h.item_nome}</td>
                      <td className="text-right py-2 font-medium">{formatNumber(h.quantidade)}</td>
                      <td className="py-2">{h.galpao_nome}</td>
                      <td className="py-2 text-muted-foreground">{h.lote_codigo || "-"}</td>
                      <td className="text-right py-2">{formatNumber(h.saldo_anterior)}</td>
                      <td className="text-right py-2 font-medium">{formatNumber(h.saldo_posterior)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/consumo')({
  component: ConsumoPage,
})
